import * as React from 'react'
import Input from '@frontrow/console/src/entities/components/base/input'
import {Button} from '@frontrow/console/src/components/button'
import {Link} from '@frontrow/console/src/components/ui/link'
import {H3} from '@frontrow/console/src/components/typography'
import {type PolyfilledEvent} from '@frontrow/console/src/polyfills'

import ErrorAlert from '@frontrow/console/src/components/common/error-alert'
import WarningIcon from '@frontrow/console/src/components/common/warning-icon'
import {
  jobsFormContainer,
  jsonTextArea,
  retryCheckbox,
  scheduleCheckbox,
  dateTimePicker,
  dateTimeError,
  submitButton,
  errorMessage,
  successMessage,
  jsonStatusError,
  jsonStatusOk,
  backfillWarning
} from './jobs-form.module.scss'

type Props = {
  canSubmit: boolean
  message: string | undefined | null
  error: string | undefined | null
  jsonError: string | undefined | null
  jsonText: string
  retryFailedJobs: boolean
  onChangeJsonText: (text: string) => void
  onToggleRetryFailedJobs: () => void
  onSubmit: () => void
  scheduleForLater: boolean
  onToggleScheduleForLater: () => void
  dateTime: Date | undefined
  validDateTime: boolean
  onChangeDateTime: (dateTime: string) => void
}

function toDateTimeLocalValue(date: Date): string {
  const y = date.getFullYear()
  const mo = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const h = String(date.getHours()).padStart(2, '0')
  const mi = String(date.getMinutes()).padStart(2, '0')
  return `${y}-${mo}-${d}T${h}:${mi}`
}

function JobsForm(props: Props): React.ReactElement {
  const {
    canSubmit,
    message,
    error,
    jsonError,
    jsonText,
    retryFailedJobs,
    onToggleRetryFailedJobs,
    scheduleForLater,
    onToggleScheduleForLater,
    dateTime,
    validDateTime,
    onChangeDateTime,
    onChangeJsonText,
    onSubmit
  } = props

  const onChangeJsonTextHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.currentTarget instanceof HTMLTextAreaElement) {
      const target: HTMLTextAreaElement = e.currentTarget
      onChangeJsonText(target.value)
    } else {
      throw new Error(
        'Expected event.currentTarget to be HTMLTextAreaElement for onChangeAddressText'
      )
    }
  }

  const onSubmitHandler = (e: PolyfilledEvent) => {
    e.preventDefault()
    if (canSubmit) {
      onSubmit()
    }
  }

  const messageNode =
    message === null || message === undefined ? null : (
      <div className={successMessage}>{message}</div>
    )

  const errorNode =
    error === null || error === undefined ? null : (
      <div className={errorMessage}>
        <ErrorAlert>{error}</ErrorAlert>
      </div>
    )

  const jsonStatusNode =
    jsonError === null || jsonError === undefined ? (
      <div className={jsonStatusOk}>Syntax ok</div>
    ) : (
      <div className={jsonStatusError}>{jsonError}</div>
    )

  const dateTimePickerClass = validDateTime ? dateTimePicker : `${dateTimePicker} ${dateTimeError}`

  return (
    <div className={jobsFormContainer}>
      <H3>Spawn Background Jobs on the Default Queue</H3>
      <ul>
        <li>
          The backend only accepts JSON that parses into <code>Array&lt;DefaultJob&gt;</code>
        </li>
        <li>Feel free to ask for help in #engineering.</li>
      </ul>
      <form onSubmit={onSubmitHandler}>
        <div className={retryCheckbox}>
          <Input
            id="retry-failed-jobs-checkbox"
            checked={retryFailedJobs}
            type="checkbox"
            onChange={onToggleRetryFailedJobs}
          />{' '}
          <label htmlFor="retry-failed-jobs-checkbox">Retry failed jobs?</label>
        </div>
        <div className={scheduleCheckbox}>
          <Input
            id="schedule-at-checkbox"
            checked={scheduleForLater}
            type="checkbox"
            onChange={onToggleScheduleForLater}
          />{' '}
          <label htmlFor="schedule-at-checkbox">Schedule this job for later?</label>
        </div>
        {scheduleForLater ? (
          <div className={dateTimePickerClass}>
            <input
              type="datetime-local"
              value={dateTime ? toDateTimeLocalValue(dateTime) : ''}
              onChange={e => onChangeDateTime(e.target.value)}
            />
          </div>
        ) : null}
        <div className={backfillWarning}>
          <WarningIcon />
          <b>
            Remember to run backfill jobs in{' '}
            <Link href="https://illuminate.atlassian.net/wiki/spaces/EN/pages/17185944880/Backfills#Non-production">
              all environments
            </Link>{' '}
            to avoid breaking CD
          </b>
          <WarningIcon />
        </div>
        <div>
          <textarea
            className={jsonTextArea}
            onChange={onChangeJsonTextHandler}
            placeholder="Paste jobs as an array of objects here"
            required
            rows={20}
            value={jsonText}
          />
          {jsonStatusNode}
        </div>
        <Button addClass={submitButton} disabled={!canSubmit} onClick={onSubmitHandler}>
          Spawn Job(s)
        </Button>
      </form>
      {messageNode}
      {errorNode}
    </div>
  )
}

export default JobsForm
